<template>
  <DsPageContent>
    <!-- Hero -->
    <DsHeroWithMedia
      v-if="content.hero"
      theme="grey"
      :screen="screen"
      :title="content.hero.title"
      :image="content.hero.image"
      :links="content.hero.links"
      :eyebrow="content.hero.eyebrow"
      :icon="content.hero.icon"
    >
      <template
        v-if="content.hero.description"
        #description
      >
        <RichText
          :data="content.hero.description"
          class="ds-text-medium"
        />
      </template>
    </DsHeroWithMedia>
    <!-- Benefits -->
    <DsSection v-if="content.benefitsCollection">
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 4 }"
        :items="content.benefitsCollection"
      >
        <template #default="{ item }">
          <DsCardTextIcon
            v-if="item"
            :icon="item.icon"
            :title="item.title"
          >
            <RichText
              v-if="item.description"
              :data="item.description"
            />
          </DsCardTextIcon>
        </template>
      </DsGrid>
    </DsSection>
    <DsSection
      :with-space="false"
      aria-hidden="true"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsDivider />
        </div>
      </div>
    </DsSection>
    <DsSectionGroup
      :title="content.contentBlock1.title"
      :subtitle="content.contentBlock1.description"
      centered
      with-title-size-large
      class="section-group__title--no-margin"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsMediaTextBlock
            v-for="(mediaTextItem, index) in content.contentBlock1.items"
            :key="index"
            :title="mediaTextItem.title"
            :image="mediaTextItem.image"
            :orientation="index % 2 ? 'left' : 'right'"
            :theme="index % 2 ? 'dark-grey' : 'light'"
            :screen="screen"
            :link="mediaTextItem.link"
            contained
          >
            <RichText
              v-if="content.contentBlock1.items[0].description"
              :data="content.contentBlock1.items[0].description"
            />
          </DsMediaTextBlock>
        </div>
      </div>
    </DsSectionGroup>
    <DsSectionGroup
      v-if="content.contentBlock2"
      :title="content.contentBlock2.title"
      :subtitle="content.contentBlock2.description"
      :link="content.contentBlock2.link"
      theme="grey"
    >
      <DsGrid
        :column-width="{ small: 12, medium: 6, large: 3 }"
        :items="content.contentBlock2.items"
      >
        <template #default="{ item }">
          <DsCardMediaWithImage
            :title="item.title"
            :image="item.image"
            :screen="screen"
            with-title-size-small
          >
            <RichText :data="item.description" />
          </DsCardMediaWithImage>
        </template>
      </DsGrid>
    </DsSectionGroup>
    <DsSection
      v-if="content.contentBlock2 && content.contentBlock3 && !content.callOut"
      :with-space="false"
      theme="grey"
      aria-hidden="true"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsDivider />
        </div>
      </div>
    </DsSection>
    <DsSection
      v-if="content.callOut"
      :with-space="false"
      theme="grey"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsMediaTextSection v-bind="content.callOut" />
        </div>
      </div>
    </DsSection>
    <DsSection
      v-if="content.contentBlock3"
      theme="grey"
    >
      <DsMediaTextBlock
        orientation="right"
        :title="content.contentBlock3.title"
        :image="content.contentBlock3.image"
        :screen="screen"
        with-shadow
      >
        <RichText
          v-if="content.contentBlock3.description"
          :data="content.contentBlock3.description"
          class="ds-list-style-checked ds-list-style-checked--green ds-list-font-default"
        />
      </DsMediaTextBlock>
    </DsSection>
    <DsQuote
      v-if="content.quote"
      :contained="false"
      :logo="content.quote.logo"
      :text="content.quote.quote"
      :author="content.quote.author"
      :extra-info="content.quote.role"
    />
    <DsSection
      v-if="content.logoBar"
      :with-space-top="false"
    >
      <DsLogoBar
        :items="content.logoBar.items"
        align-center
      />
    </DsSection>
    <DsSectionImage
      v-if="content.media"
      :with-space-top="false"
      theme-variant="light-grey"
    >
      <div class="ds-grid">
        <div class="ds-grid__col">
          <DsImageElement
            :image="content.media"
            :screen="screen"
          />
        </div>
      </div>
    </DsSectionImage>
    <DsSectionGroup
      v-if="content.contentBlock4"
      :title="content.contentBlock4.title"
      :subtitle="content.contentBlock4.description"
      :link="content.contentBlock4.links"
      theme="grey"
      centered
    >
      <DsGrid
        :column-width="{ small: 12, medium: 4, large: 4 }"
        :items="content.contentBlock4.items"
        class="ds-justify-content-center ds-margin-top-24"
      >
        <template #default="{ item }">
          <DsCardTextIcon
            v-if="item"
            :icon="item.icon"
            :title="item.title"
            alignment="center"
          >
            <RichText
              v-if="item.description"
              :data="item.description"
            />
          </DsCardTextIcon>
        </template>
      </DsGrid>
    </DsSectionGroup>
    <DsSectionGroup
      v-if="content.contentBlock5"
      :title="content.contentBlock5.title"
      :subtitle="content.contentBlock5.description"
      full-screen
    >
      <DsTabs
        v-if="content.contentBlock5.items.length"
        :label="content.contentBlock5.title"
        :screen="screen"
        contained
        use-grid
        use-grid-divider
        contained-content
        contained-divider
      >
        <DsTabItem
          v-for="(tab, index) in content.contentBlock5.items"
          :key="`tab-${index}`"
          :title="tab.title"
        >
          <div
            v-for="(tabSubItem, i) in tab.items"
            :key="i"
            class="ds-margin-top-48"
          >
            <div class="ds-grid">
              <div class="ds-grid__col">
                <h3 class="ds-heading-small">
                  {{ tabSubItem.title }}
                </h3>
                <p class="ds-text ds-margin-top-12">
                  {{ tabSubItem.description }}
                </p>
              </div>
            </div>
            <DsGrid
              :column-width="{ small: 12, medium: 6, large: 4 }"
              :items="tabSubItem.items"
              class="ds-margin-top-24 ds-md-margin-top-48"
            >
              <template #default="{ item }">
                <DsCardMediaWithImage
                  :title="item.title"
                  :image="item.image"
                  :screen="screen"
                  aspect-ratio="16/9"
                  with-title-size-small
                >
                  <RichText :data="item.description" />
                </DsCardMediaWithImage>
              </template>
            </DsGrid>
          </div>
        </DsTabItem>
      </DsTabs>
    </DsSectionGroup>
    <DsSection
      v-if="content.contentBlock6"
      theme="grey"
    >
      <DsLogoBar
        :title="content.contentBlock6.title"
        :link="content.contentBlock6.link"
        :items="content.contentBlock6.items"
        orientation="horizontal"
      >
        <template #description>
          <RichText :data="content.contentBlock6.description" />
        </template>
      </DsLogoBar>
    </DsSection>
    <DsCallToAction
      v-if="content.cta"
      :title="content.cta.title"
      :text="content.cta.text"
      :primary-link="content.cta.primaryLink"
      :secondary-link="content.cta.secondaryLink"
    />
  </DsPageContent>
</template>

<script setup>
import {
  DsCallToAction,
  DsCardMediaWithImage,
  DsImageElement,
  DsCardTextIcon,
  DsDivider,
  DsGrid,
  DsHeroWithMedia,
  DsLogoBar,
  DsMediaTextBlock,
  DsMediaTextSection,
  DsPageContent,
  DsQuote,
  DsSection,
  DsSectionGroup,
  DsSectionImage,
  DsTabItem,
  DsTabs
} from 'awds'
import { PageProductPosPayments } from '#layers/core/data-layer/definitions/products'
import RichText from '#layers/core/components/RichText.vue'

definePageMeta({
  pageTransition: {
    name: 'page-fade'
  }
})

const { locale: localeRef } = useI18n()
const { onBeforeAdyenData, onAfterAdyenData, useStandardAsyncData, content } = useMainPageData()
const { screen } = useScreen()
const { setNotification } = useNotificationBanner()

await onBeforeAdyenData({
  withRoutes: true,
  globalDataContentType: PageProductPosPayments.contentType
})

const { data } = await useAsyncData(`product-pos-payments-${localeRef.value}`, async () => {
  const pageData = await useStandardAsyncData(PageProductPosPayments)
  if (!pageData || !Object.keys(pageData).length) {
    return null
  }
  return pageData
})

await onAfterAdyenData(data.value)

onBeforeMount(() => {
  setNotification(data.value?.pageData?.content?.notificationBar)
})
</script>

<style lang="scss" scoped>
.section-group__title--no-margin {
  :deep() .ds-grid__col:has(> .ds-heading) {
    margin-bottom: 0;
  }
}
</style>
